<template>
  <b-modal
    :visible="value"
    centered
    size="lg"
    hide-footer
    no-close-on-backdrop
    dialog-class="att-dialog"
    content-class="att-content"
    body-class="p-0"
    @change="$emit('input', $event)"
    @show="load"
    @hidden="reset"
  >
    <!-- Header -->
    <template #modal-header="{ close }">
      <div class="att-head">
        <div class="att-head__icon">
          <feather-icon icon="PaperclipIcon" size="18" />
        </div>
        <div class="att-head__text">
          <h5 class="att-head__title">
            {{ title }}
          </h5>
          <div class="att-head__meta">
            <span>{{ items.length }} file{{ items.length === 1 ? '' : 's' }}</span>
            <span
              v-if="missingCount > 0"
              class="att-chip att-chip--missing"
            >
              <feather-icon icon="AlertTriangleIcon" size="12" />
              {{ missingCount }} missing
            </span>
            <span
              v-else-if="checking"
              class="att-chip att-chip--checking"
            >
              <span class="att-spinner" />
              verifying files…
            </span>
          </div>
        </div>
        <button
          type="button"
          class="att-icon-btn"
          aria-label="Close"
          @click="close()"
        >
          <feather-icon icon="XIcon" size="18" />
        </button>
      </div>
    </template>

    <div class="att-body">
      <!-- File list -->
      <div
        v-if="loading"
        class="att-skeletons"
      >
        <div
          v-for="n in 2"
          :key="n"
          class="att-skeleton"
        />
      </div>

      <ul
        v-else-if="items.length"
        class="att-list"
      >
        <li
          v-for="item in items"
          :key="item.document_id"
          class="att-item"
          :class="{ 'att-item--missing': item.state === 'missing' }"
        >
          <div
            class="att-file-icon"
            :class="`att-file-icon--${fileKind(item.document_file_name)}`"
          >
            <feather-icon
              :icon="fileIcon(item.document_file_name)"
              size="20"
            />
          </div>

          <div class="att-item__main">
            <div
              class="att-item__name"
              :title="item.document_file_name"
            >
              {{ item.document_file_name }}
            </div>
            <div class="att-item__sub">
              <span
                class="att-chip"
                :class="`att-chip--${item.state}`"
              >
                <span
                  v-if="item.state === 'checking'"
                  class="att-spinner"
                />
                <feather-icon
                  v-else
                  :icon="stateIcon(item.state)"
                  size="12"
                />
                {{ stateLabel(item.state) }}
              </span>
              <span
                v-if="item.document_created_time"
                class="att-item__date"
              >{{ formatDate(item.document_created_time) }}</span>
            </div>
            <div
              v-if="item.state === 'missing'"
              class="att-item__hint"
            >
              File not found in storage. Delete this entry and upload it again.
            </div>
          </div>

          <div class="att-item__actions">
            <template v-if="confirmId === item.document_id">
              <span class="att-confirm__text">Delete?</span>
              <button
                type="button"
                class="att-btn att-btn--danger"
                :disabled="busyId === item.document_id"
                @click="removeItem(item)"
              >
                <span
                  v-if="busyId === item.document_id"
                  class="att-spinner att-spinner--light"
                />
                <template v-else>
                  Yes
                </template>
              </button>
              <button
                type="button"
                class="att-btn att-btn--ghost"
                @click="confirmId = null"
              >
                No
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="att-icon-btn"
                title="View in browser"
                aria-label="View in browser"
                :disabled="!canOpen(item) || busyId === item.document_id"
                @click="openItem(item, false)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </button>
              <button
                type="button"
                class="att-icon-btn"
                title="Download"
                aria-label="Download"
                :disabled="!canOpen(item) || busyId === item.document_id"
                @click="openItem(item, true)"
              >
                <feather-icon icon="DownloadIcon" size="16" />
              </button>
              <button
                v-if="!readonly"
                type="button"
                class="att-icon-btn att-icon-btn--danger"
                title="Delete"
                aria-label="Delete"
                @click="confirmId = item.document_id"
              >
                <feather-icon icon="TrashIcon" size="16" />
              </button>
            </template>
          </div>
        </li>
      </ul>

      <div
        v-else
        class="att-empty"
      >
        <feather-icon icon="FileIcon" size="28" />
        <div>No files attached yet</div>
      </div>

      <!-- Upload -->
      <div
        v-if="!readonly"
        class="att-upload"
      >
        <div
          class="att-drop"
          :class="{ 'att-drop--over': dragging, 'att-drop--disabled': uploading }"
          role="button"
          tabindex="0"
          aria-label="Drop files here or click to browse"
          @click="pick"
          @keydown.enter.prevent="pick"
          @keydown.space.prevent="pick"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <div class="att-drop__icon">
            <feather-icon icon="UploadCloudIcon" size="22" />
          </div>
          <div class="att-drop__title">
            <strong>Drop files here</strong> or <span class="att-link">browse</span>
          </div>
          <div class="att-drop__hint">
            Max {{ maxMb }}MB per file
          </div>
          <input
            ref="input"
            type="file"
            multiple
            hidden
            @change="onPick"
          >
        </div>

        <ul
          v-if="queue.length"
          class="att-queue"
        >
          <li
            v-for="(q, i) in queue"
            :key="q.id"
            class="att-queue__item"
          >
            <div class="att-queue__row">
              <span
                class="att-queue__name"
                :title="q.name"
              >{{ q.name }}</span>
              <span class="att-queue__size">{{ formatSize(q.size) }}</span>
              <span
                v-if="q.status === 'done'"
                class="att-chip att-chip--ok"
              >Uploaded</span>
              <span
                v-else-if="q.status === 'error'"
                class="att-chip att-chip--missing"
                :title="q.error"
              >Failed</span>
              <button
                v-else-if="q.status === 'queued'"
                type="button"
                class="att-icon-btn att-icon-btn--sm"
                aria-label="Remove from queue"
                @click="queue.splice(i, 1)"
              >
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
            <div
              v-if="q.status === 'uploading' || q.status === 'done'"
              class="att-progress"
            >
              <div
                class="att-progress__bar"
                :style="{ width: `${q.progress}%` }"
              />
            </div>
            <div
              v-if="q.status === 'error'"
              class="att-queue__error"
            >
              {{ q.error }}
            </div>
          </li>
        </ul>

        <div
          v-if="pendingCount > 0 || uploading"
          class="att-upload__bar"
        >
          <button
            type="button"
            class="att-btn att-btn--primary"
            :disabled="uploading || pendingCount === 0"
            @click="uploadAll"
          >
            <span
              v-if="uploading"
              class="att-spinner att-spinner--light"
            />
            {{ uploading ? 'Uploading…' : `Upload ${pendingCount} file${pendingCount === 1 ? '' : 's'}` }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import axios from 'axios'
import moment from 'moment-timezone'
import apiEndpoints from '@/constants/apiEndpoints'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// type -> { route: documents/<route>/:id, field: multipart FK field used by documents/create }
const TYPES = {
  mr: { route: 'mr', field: 'document_material_request_id' },
  pr: { route: 'pr', field: 'document_purchase_request_id' },
  po: { route: 'po', field: 'document_purchase_order_id' },
  transfer: { route: 'transfer', field: 'document_transfer_id' },
  payment_request: { route: 'payment_request', field: 'document_payment_request_id' },
  stock_opname: { route: 'stock-opname', field: 'document_stock_opname_id' },
}

const MAX_MB = 20
const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.txt']

export default {
  components: { BModal },
  props: {
    value: { type: Boolean, default: false },
    type: { type: String, required: true, validator: v => Object.keys(TYPES).includes(v) },
    parentId: { type: [Number, String], default: null },
    title: { type: String, default: 'Documents' },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      loading: false,
      checking: false,
      confirmId: null,
      busyId: null,
      dragging: false,
      uploading: false,
      queue: [],
      maxMb: MAX_MB,
      seq: 0,
      loadToken: 0,
    }
  },
  computed: {
    missingCount() { return this.items.filter(i => i.state === 'missing').length },
    pendingCount() { return this.queue.filter(q => q.status === 'queued').length },
  },
  methods: {
    headers() {
      return { Authorization: `Bearer ${this.$cookies.get('userToken')}` }
    },
    url(path) { return `${process.env.VUE_APP_API_URL}${path}` },
    toast(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({ component: ToastificationContent, props: { title, icon, variant } })
    },

    reset() {
      this.items = []
      this.queue = []
      this.confirmId = null
      this.busyId = null
      this.dragging = false
      this.loadToken += 1
    },

    // ── List + live existence check ──────────────────────────────────────
    async load() {
      if (this.parentId == null || this.parentId === '') return
      this.loadToken += 1
      const token = this.loadToken
      this.loading = true
      try {
        const res = await axios.get(
          this.url(`${apiEndpoints.API_GET_DOCUMENTS}${TYPES[this.type].route}/${this.parentId}`),
          { headers: this.headers() },
        )
        if (token !== this.loadToken) return
        const rows = (res.data && res.data.success && res.data.data) || []
        this.items = rows.map(d => ({ ...d, state: d.document_old ? 'legacy' : 'checking' }))
      } catch (e) {
        if (token !== this.loadToken) return
        this.items = []
        this.toast('Failed to load documents', 'danger', 'AlertTriangleIcon')
      } finally {
        if (token === this.loadToken) this.loading = false
      }
      this.verify(token)
    },
    async verify(token) {
      const ids = this.items.filter(i => i.state === 'checking').map(i => i.document_id)
      if (!ids.length) return
      this.checking = true
      try {
        const res = await axios.post(this.url('documents/check'), { ids }, { headers: this.headers() })
        if (token !== this.loadToken) return
        const byId = {}
        const results = (res.data && res.data.data) || []
        results.forEach(r => { byId[r.document_id] = r.file_status })
        this.items.forEach(i => {
          if (i.state !== 'checking') return
          const s = byId[i.document_id]
          i.state = s === 'ok' || s === 'missing' || s === 'legacy' ? s : 'unverified'
        })
      } catch (e) {
        if (token === this.loadToken) this.items.forEach(i => { if (i.state === 'checking') i.state = 'unverified' })
      } finally {
        if (token === this.loadToken) this.checking = false
      }
    },

    // ── View / download / delete ─────────────────────────────────────────
    canOpen(item) { return item.state === 'ok' || item.state === 'unverified' || item.state === 'checking' },
    async openItem(item, download) {
      // Open the tab synchronously (inside the click) so popup blockers allow it.
      const win = download ? null : window.open('', '_blank')
      this.busyId = item.document_id
      try {
        const res = await axios.post(this.url(apiEndpoints.API_DOWNLOAD_ASSET), {
          ObjectName: `${item.document_number || ''}${item.document_file_name}`,
          FileName: item.document_file_name,
          Download: download,
        }, { headers: this.headers() })
        if (res.data.Status !== 1 || !res.data.Payload) throw new Error('no url')
        if (download) {
          const a = document.createElement('a')
          a.href = res.data.Payload
          a.rel = 'noopener'
          document.body.appendChild(a)
          a.click()
          a.remove()
        } else if (win) {
          win.location.href = res.data.Payload
        }
      } catch (e) {
        if (win) win.close()
        this.toast('Could not open the file', 'danger', 'AlertTriangleIcon')
      } finally {
        this.busyId = null
      }
    },
    async removeItem(item) {
      this.busyId = item.document_id
      try {
        const res = await axios.get(this.url(apiEndpoints.API_DELETE_DOCUMENT), {
          params: { id: item.document_id },
          headers: this.headers(),
        })
        if (!res.data.success) throw new Error(res.data.Message || 'failed')
        this.toast('Attachment deleted')
        this.confirmId = null
        this.$emit('changed')
        await this.load()
      } catch (e) {
        this.toast('Delete failed', 'danger', 'AlertTriangleIcon')
      } finally {
        this.busyId = null
      }
    },

    // ── Upload ───────────────────────────────────────────────────────────
    nextId() {
      this.seq += 1
      return this.seq
    },
    pick() { if (!this.uploading && this.$refs.input) this.$refs.input.click() },
    onPick(e) {
      this.enqueue(e.target.files)
      e.target.value = ''
    },
    onDrop(e) {
      this.dragging = false
      if (!this.uploading) this.enqueue(e.dataTransfer.files)
    },
    enqueue(fileList) {
      Array.from(fileList || []).forEach(file => {
        const tooBig = file.size > MAX_MB * 1024 * 1024
        const dot = file.name.lastIndexOf('.')
        const ext = dot > 0 ? file.name.slice(dot).toLowerCase() : ''
        let error = ''
        if (!ext) error = 'File has no extension - rename it with one (e.g. .pdf, .jpg)'
        else if (!ALLOWED_EXT.includes(ext)) error = `File type ${ext} is not allowed`
        else if (tooBig) error = `Larger than ${MAX_MB}MB`
        this.queue.push({
          id: this.nextId(),
          file,
          name: file.name,
          size: file.size,
          status: error ? 'error' : 'queued',
          error,
          progress: 0,
        })
      })
    },
    async uploadOne(q) {
      /* eslint-disable no-param-reassign */
      q.status = 'uploading'
      const form = new FormData()
      form.append('file', q.file, q.file.name)
      form.append(TYPES[this.type].field, this.parentId)
      form.append('time', moment())
      try {
        const res = await axios.post(this.url(apiEndpoints.API_CREATE_DOCUMENT), form, {
          headers: this.headers(),
          onUploadProgress: p => { if (p.total) q.progress = Math.round((p.loaded / p.total) * 100) },
        })
        if (!res.data.success) throw new Error(res.data.Message || 'Upload failed')
        q.progress = 100
        q.status = 'done'
        return true
      } catch (e) {
        q.status = 'error'
        q.error = (e.response && e.response.data && e.response.data.Message) || e.message || 'Upload failed'
        return false
      }
      /* eslint-enable no-param-reassign */
    },
    async uploadAll() {
      this.uploading = true
      // Sequential on purpose: one request at a time keeps progress readable and the server calm.
      const results = await this.queue
        .filter(x => x.status === 'queued')
        .reduce((chain, q) => chain.then(async acc => [...acc, await this.uploadOne(q)]), Promise.resolve([]))
      const uploaded = results.filter(Boolean).length
      this.uploading = false
      if (uploaded) {
        this.toast(`${uploaded} file${uploaded === 1 ? '' : 's'} uploaded`)
        this.$emit('changed')
        this.queue = this.queue.filter(q => q.status === 'error')
        await this.load()
      }
    },

    // ── Presentation helpers ─────────────────────────────────────────────
    ext(name) { return (String(name || '').split('.').pop() || '').toLowerCase() },
    fileKind(name) {
      const e = this.ext(name)
      if (e === 'pdf') return 'pdf'
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'].includes(e)) return 'image'
      if (['xls', 'xlsx', 'csv'].includes(e)) return 'sheet'
      if (['doc', 'docx', 'rtf', 'txt'].includes(e)) return 'doc'
      if (['zip', 'rar', '7z'].includes(e)) return 'archive'
      return 'other'
    },
    fileIcon(name) {
      const k = this.fileKind(name)
      const icons = {
        pdf: 'FileTextIcon',
        image: 'ImageIcon',
        sheet: 'FileTextIcon',
        doc: 'FileTextIcon',
        archive: 'ArchiveIcon',
      }
      return icons[k] || 'FileIcon'
    },
    stateLabel(s) {
      const labels = {
        checking: 'Checking',
        ok: 'Available',
        missing: 'Missing',
        legacy: 'Legacy',
        unverified: 'Not verified',
      }
      return labels[s] || s
    },
    stateIcon(s) {
      const icons = {
        ok: 'CheckCircleIcon',
        missing: 'AlertTriangleIcon',
        legacy: 'ClockIcon',
        unverified: 'AlertCircleIcon',
      }
      return icons[s] || 'FileIcon'
    },
    formatDate(d) { return moment(d).format('DD MMM YYYY, HH:mm') },
    formatSize(b) {
      if (b < 1024) return `${b} B`
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
      return `${(b / 1024 / 1024).toFixed(1)} MB`
    },
  },
}
</script>

<style lang="scss">
.att-dialog {
  --att-accent: #2b7a80;
  --att-accent-soft: rgba(43, 122, 128, 0.1);
  --att-danger: #ea5455;
  --att-ok: #28a745;
  --att-border: rgba(120, 130, 150, 0.22);
  --att-surface: rgba(120, 130, 150, 0.06);
  --att-muted: #7b8497;
}
.dark-layout .att-dialog {
  --att-accent: #49b8ab;
  --att-accent-soft: rgba(73, 184, 171, 0.14);
  --att-border: rgba(255, 255, 255, 0.12);
  --att-surface: rgba(255, 255, 255, 0.04);
  --att-muted: #a3abbd;
}

.att-content { border: 0; border-radius: 16px; overflow: hidden; }
.att-content .modal-header { padding: 0; border: 0; display: block; }

.att-head {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 20px; border-bottom: 1px solid var(--att-border);
  &__icon {
    width: 38px; height: 38px; border-radius: 10px; flex: none;
    display: grid; place-items: center;
    color: var(--att-accent); background: var(--att-accent-soft);
  }
  &__text { flex: 1; min-width: 0; }
  &__title { margin: 0; font-weight: 600; }
  &__meta {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    margin-top: 2px; font-size: 12px; color: var(--att-muted);
  }
}

.att-body { padding: 18px 20px 20px; }

.att-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 9px; border-radius: 999px;
  font-size: 11px; font-weight: 600; line-height: 18px;
  color: var(--att-muted); background: var(--att-surface);
  &--ok { color: var(--att-ok); background: rgba(40, 167, 69, 0.12); }
  &--missing { color: var(--att-danger); background: rgba(234, 84, 85, 0.12); }
  &--checking, &--unverified { color: var(--att-muted); }
  &--legacy { color: #b7791f; background: rgba(237, 168, 55, 0.14); }
}

.att-list { list-style: none; margin: 0; padding: 0; max-height: 340px; overflow-y: auto; display: grid; gap: 10px; }
.att-item {
  display: flex; align-items: center; gap: 14px;
  padding: 12px 14px; border: 1px solid var(--att-border); border-radius: 12px;
  background: var(--att-surface);
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: var(--att-accent); }
  &--missing {
    border-color: rgba(234, 84, 85, 0.45); background: rgba(234, 84, 85, 0.06);
    &:hover { border-color: var(--att-danger); }
  }
  &__main { flex: 1; min-width: 0; }
  &__name { font-weight: 600; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__sub { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
  &__date { font-size: 11px; color: var(--att-muted); }
  &__hint { margin-top: 6px; font-size: 11.5px; color: var(--att-danger); }
  &__actions { display: flex; align-items: center; gap: 6px; flex: none; }
}
.att-file-icon {
  width: 42px; height: 42px; border-radius: 10px; flex: none; display: grid; place-items: center;
  color: var(--att-muted); background: var(--att-surface);
  &--pdf { color: #ea5455; background: rgba(234, 84, 85, 0.12); }
  &--image { color: #3b82f6; background: rgba(59, 130, 246, 0.12); }
  &--sheet { color: #28a745; background: rgba(40, 167, 69, 0.12); }
  &--doc { color: #6366f1; background: rgba(99, 102, 241, 0.12); }
  &--archive { color: #d98a1f; background: rgba(237, 168, 55, 0.14); }
}

.att-icon-btn {
  width: 34px; height: 34px; border: 0; border-radius: 9px; flex: none;
  display: inline-grid; place-items: center; cursor: pointer;
  color: var(--att-muted); background: transparent;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) { color: var(--att-accent); background: var(--att-accent-soft); }
  &--danger:hover:not(:disabled) { color: var(--att-danger); background: rgba(234, 84, 85, 0.12); }
  &--sm { width: 26px; height: 26px; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--att-accent); outline-offset: 2px; }
}

.att-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-width: 44px; height: 32px; padding: 0 14px; border-radius: 9px;
  font-size: 12.5px; font-weight: 600; cursor: pointer; border: 1px solid transparent;
  &--primary { height: 38px; padding: 0 20px; color: #fff; background: var(--att-accent); }
  &--danger { color: #fff; background: var(--att-danger); }
  &--ghost { color: var(--att-muted); background: transparent; border-color: var(--att-border); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--att-accent); outline-offset: 2px; }
}
.att-confirm__text { font-size: 12px; font-weight: 600; color: var(--att-danger); margin-right: 2px; }

.att-empty {
  display: grid; justify-items: center; gap: 8px; padding: 34px 0;
  color: var(--att-muted); font-size: 13px;
}
.att-skeletons { display: grid; gap: 10px; }
.att-skeleton {
  height: 68px; border-radius: 12px;
  background: linear-gradient(90deg, var(--att-surface) 25%, var(--att-border) 37%, var(--att-surface) 63%);
  background-size: 400% 100%; animation: att-shimmer 1.4s ease infinite;
}

.att-upload { margin-top: 18px; }
.att-drop {
  display: grid; justify-items: center; gap: 4px; padding: 22px 16px; cursor: pointer; text-align: center;
  border: 2px dashed var(--att-border); border-radius: 14px; background: var(--att-surface);
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover, &:focus-visible, &--over { border-color: var(--att-accent); background: var(--att-accent-soft); outline: none; }
  &--disabled { opacity: 0.55; pointer-events: none; }
  &__icon {
    width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center;
    color: var(--att-accent); background: var(--att-accent-soft); margin-bottom: 4px;
  }
  &__title { font-size: 13px; }
  &__hint { font-size: 11.5px; color: var(--att-muted); }
}
.att-link { color: var(--att-accent); font-weight: 600; text-decoration: underline; }

.att-queue { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 8px; }
.att-queue__item { padding: 8px 12px; border: 1px solid var(--att-border); border-radius: 10px; }
.att-queue__row { display: flex; align-items: center; gap: 10px; }
.att-queue__name { flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.att-queue__size { font-size: 11px; color: var(--att-muted); }
.att-queue__error { margin-top: 4px; font-size: 11.5px; color: var(--att-danger); }
.att-progress { height: 4px; margin-top: 8px; border-radius: 4px; background: var(--att-border); overflow: hidden; }
.att-progress__bar { height: 100%; background: var(--att-accent); transition: width 0.2s ease; }
.att-upload__bar { display: flex; justify-content: flex-end; margin-top: 12px; }

.att-spinner {
  width: 12px; height: 12px; border-radius: 50%; display: inline-block;
  border: 2px solid var(--att-border); border-top-color: var(--att-accent);
  animation: att-spin 0.7s linear infinite;
  &--light { border-color: rgba(255, 255, 255, 0.4); border-top-color: #fff; }
}
@keyframes att-spin { to { transform: rotate(360deg); } }
@keyframes att-shimmer { to { background-position: -135% 0; } }

@media (max-width: 575px) {
  .att-item { flex-wrap: wrap; }
  .att-item__actions { width: 100%; justify-content: flex-end; }
}
@media (prefers-reduced-motion: reduce) {
  .att-spinner, .att-skeleton { animation: none; }
}
</style>
